import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

export interface AuthenticatedUser {
  id: string
  name: string
  username: string
  email: string | null
  type: string
  status: string
  slug: string | null
  hierarchy?: {
    id: string
    name: string
    authorities: string[]
  }
  [key: string]: unknown
}

interface ImpersonationSnapshot {
  user: AuthenticatedUser
  accessToken: string
  label: string
}

interface AuthState {
  user: AuthenticatedUser | null
  accessToken: string | null
  /**
   * PILHA de logins originais durante impersonação ANINHADA. Ex.: admin →
   * diretor → supervisor empilha [admin, diretor]; cada "Sair da visualização"
   * desempilha um nível até voltar à origem.
   */
  impersonationStack: ImpersonationSnapshot[]
  setAuth: (user: AuthenticatedUser, token: string) => void
  /** Atualiza só o usuário (mantém o token) — usado no refresh de permissões. */
  setUser: (user: AuthenticatedUser) => void
  clearAuth: () => void
  /** Entra como outro usuário, empilhando o login atual para retorno. */
  startImpersonation: (user: AuthenticatedUser, token: string, label?: string) => void
  /** Desempilha um nível; retorna o snapshot restaurado (para navegação). */
  stopImpersonation: () => ImpersonationSnapshot | null
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      impersonationStack: [],
      setAuth: (user, token) => set({ user, accessToken: token }),
      setUser: (user) => set({ user }),
      clearAuth: () => set({ user: null, accessToken: null, impersonationStack: [] }),
      startImpersonation: (user, token) => {
        const { user: curUser, accessToken: curToken, impersonationStack } = get()
        if (! curUser || ! curToken) {
          set({ user, accessToken: token })
          return
        }
        const snap: ImpersonationSnapshot = { user: curUser, accessToken: curToken, label: curUser.name }
        set({ user, accessToken: token, impersonationStack: [...impersonationStack, snap] })
      },
      stopImpersonation: () => {
        const stack = get().impersonationStack
        if (stack.length === 0) return null
        const snap = stack[stack.length - 1]
        set({ user: snap.user, accessToken: snap.accessToken, impersonationStack: stack.slice(0, -1) })
        return snap
      },
    }),
    {
      name: 'amplamaisv2.auth',
      storage: createJSONStorage(() => localStorage),
    },
  ),
)
